/**
 * FEATURE FLAGS (aka "Flippers")
 * =================
 * These are flagged-in features that we control.
 * To manage these features, go to: https://devportal.githubapp.com/feature-flags
 */
export const clientFeatureFlags = [
  // This method demonstrates usage of this service, but it isn't a real feature.
  'memex_beta_with_dummy_feature',

  // Whether to show the Group by assignees, milestones for historical charts
  'memex_historical_charts_on_assignees_milestones',

  // Tasks list block gate the tracks column and all things related to hierarchy
  'tasklist_block',

  // Enable "group by distinct" for multi-value fields like assignees
  'memex_group_by_multi_value_changes',

  // Enables the resync index button for project admins for reindexing Elasticsearch for Memex Without limits
  'memex_resync_index',

  // Controls whether automations are enabled or not, this is not a real FF, it delegates to enterprise business config
  'memex_automation_enabled',

  // Disable file upload in the side-panel for draft issues
  'memex_disable_draft_issue_file_upload',

  // Disable autofocusing first item in project views
  'memex_disable_autofocus',

  // Enable project status updates
  'memex_status_updates_notifications',

  // ensures that the projects classic UI is enabled
  'projects_classic_sunset_override',

  //Improve rendering performance of PWL table view
  'memex_mwl_table_cell_perf',

  // Enable keyboard navigation for roadmap marker nubs
  'memex_roadmap_focusable_marker_nubs',

  // Update the labels picker to not have a loading state and cache the results better
  'issues_react_remove_labels_loading',

  // Show a warning banner when a user types a low-quality comment (e.g., "+1", "ok")
  'issues_react_low_quality_comment_warning',

  // Make issue creation the default omnibar action instead of draft creation
  'memex_omnibar_prioritize_create_issue',

  // Disable the custom notification subscription UI in Issues React for users who use Newsies (instead of Notifyd)
  'notifyd_issue_watch_activity_notify',
  'notifyd_enable_issue_thread_subscriptions',

  // Adjust the UI styling to improve accessibility on small viewports
  'memex_small_viewport_a11y',

  // Enable dnd-kit's touch-specific dragging so that in board view, users can both scroll and drag on a touch device
  'memex_touch_to_drag',

  // Enable project setting form field to set default issue create repository
  'memex_default_issue_create_repository',

  // Enables 'edit field' option for grouped by field.
  'memex_grouped_by_edit_route',

  // Enable issue fields in projects
  'issue_fields',

  // Enable the "Add field" button in issue metadata
  'issue_metadata_add_field_button',

  // Use more stable query experience for fetching milestones
  'memex_milestone_caching',

  // Reduce layout shifts when changing query or sumFields in views
  'memex_reduce_view_shifts',

  // Reduce layout shifts when enabling hierarchy in views
  'memex_reduce_view_shifts_hierarchy',

  // Reduce layout shifts when changing slicing or sorting
  'memex_reduce_view_shifts_slice_sort',

  // Show progress bar when reducing layout shifts
  'memex_reduce_view_shifts_progress_bar',

  // Reduce layout shifts for columns in the board view
  'memex_reduce_view_shifts_columns',

  // Enable nested sub-issues in Projects table views
  'memex_nested_sub_issues',
  'memex_nested_sub_issues_internal_dev',

  // Enable drag and drop for nested sub-issues in Projects table views
  'memex_nested_sub_issues_drag_and_drop',

  // Enable creating a project from a query in the template dialog box
  'memex_create_project_from_query',

  // Enable advanced search filters in project item import
  'memex_project_item_import_advanced_search',

  // Enable AI reporting with Scribe
  'scribe_reporting_enabled',

  // Enable display button menu in the memex views
  'memex_display_button_config_menu',

  // Assign the actor alongside the agent when assigning Copilot to an issue
  'issues_cca_assign_actor_with_agent',

  // Enable Copilot in Projects experience
  'projects_copilot_task_experience',

  // Enable "Move items to iteration" action in group header menus
  'memex_move_items_to_iteration',

  // Enable Copilot task status
  'projects_copilot_tasks_status',
]

export const projectActorFeatureFlags = []

export const featureFlags = [...clientFeatureFlags, ...projectActorFeatureFlags] as const

export type ClientFeatureFlags = (typeof clientFeatureFlags)[number]
export type ProjectActorFeatureFlags = (typeof projectActorFeatureFlags)[number]
export type FeatureFlags = (typeof featureFlags)[number]

/**
 * FEATURE PREVIEWS
 * =================
 * These are opt-in features available to users.
 * To manage these features, go to: https://admin.github.com/devtools/toggleable_features
 */
export const featurePreviews = [] as const

export type FeaturePreviews = (typeof featurePreviews)[number]

/**
 * FEATURE GATES
 * =================
 * These are features gated by the billing plan of the memex owner org/user.
 * Feature gates are specified in plans.yaml files, as described here:
 * https://github.com/github/githubber-content/blob/691749f4be532371dbb7aa65a8b2440e68f6e673/docs/technology/dotcom/plan-feature-gates.md
 */
export const featureGates = [] as const

export type FeatureGates = (typeof featureGates)[number]

/**
 * ENABLED FEATURES
 * =================
 * Projects don't need to know where a feature is enabled from, only the state.
 * Given that, we merge all feature flags, previews, and gates into a single array.
 */
export type EnabledFeatures = FeaturePreviews | FeatureFlags | FeatureGates
export const enabledFeatures = [...featurePreviews, ...featureFlags, ...featureGates]

export type EnabledFeaturesMap = {[P in EnabledFeatures]: boolean}

export const allFeaturesDisabled = enabledFeatures.reduce((map, current) => {
  map[current] = false
  return map
}, {} as EnabledFeaturesMap)
